import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Tooltip,
  Badge,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Insights as InsightsIcon,
  Schedule as ScheduleIcon,
  Tag as TagIcon,
  ContentCopy as ContentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { useToast } from '../contexts/ToastContext';
import { useClients } from '../contexts/ClientContext';
import { competitorsAPI } from '../services/api';
import { SHOW_INSTAGRAM_UI } from '../config/featureFlags';
import { parseApiUtcDate } from '../utils/datetime';
import { formatApiError } from '../utils/postPublishErrors';

const SCRAPE_STALE_HOURS = 24;
const SCRAPE_POLL_INTERVAL_MS = 5000;
const SCRAPE_POLL_TIMEOUT_MS = 5 * 60 * 1000;

function formatScrapeTimestamp(raw) {
  const d = parseApiUtcDate(raw);
  if (!d) return 'Never';
  return d.toLocaleString();
}

function isScrapeStale(raw) {
  const d = parseApiUtcDate(raw);
  if (!d) return true;
  const hours = (Date.now() - d.getTime()) / 3600000;
  return hours > SCRAPE_STALE_HOURS;
}

const Competitors = () => {
  const { showSuccess, showError } = useToast();
  const { clients } = useClients();
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // create, edit, view
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [competitorToDelete, setCompetitorToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState({
    client_id: '',
    industry: '',
    threat_level: '',
    competitor_type: '',
    search: '',
  });
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    website: '',
    facebook_url: '',
    instagram_url: '',
    linkedin_url: '',
    youtube_url: '',
    twitter_url: '',
    tiktok_url: '',
    industry: '',
    target_audience: '',
    company_size: '',
    location: '',
    notes: '',
    competitor_type: 'direct',
    threat_level: 'medium',
    market_share: 0,
    budget_estimate: 0,
    content_quality_score: 0,
    brand_sentiment: 0,
    client_id: '',
  });
  const [scrapingById, setScrapingById] = useState({});
  const scrapePollTimers = useRef({});

  useEffect(() => {
    return () => {
      Object.values(scrapePollTimers.current).forEach((timerId) => {
        clearInterval(timerId);
      });
    };
  }, []);

  const updateCompetitorScrapeFields = useCallback((competitorId, status) => {
    setCompetitors((prev) =>
      prev.map((c) =>
        c.competitor_id === competitorId
          ? {
              ...c,
              last_scraped_at: status.last_scraped_at ?? c.last_scraped_at,
              last_scraped: status.last_scraped ?? status.last_scraped_at ?? c.last_scraped,
              last_scrape_error:
                status.last_scrape_error !== undefined
                  ? status.last_scrape_error
                  : c.last_scrape_error,
            }
          : c
      )
    );
  }, []);

  const stopScrapePolling = useCallback((competitorId) => {
    const timerId = scrapePollTimers.current[competitorId];
    if (timerId) {
      clearInterval(timerId);
      delete scrapePollTimers.current[competitorId];
    }
    setScrapingById((prev) => {
      if (!prev[competitorId]) return prev;
      const next = { ...prev };
      delete next[competitorId];
      return next;
    });
  }, []);

  const startScrapePolling = useCallback(
    (competitorId, baseline) => {
      stopScrapePolling(competitorId);
      const startedAt = Date.now();
      const baselineScrapedAt = baseline?.last_scraped_at ?? null;
      const baselineError = baseline?.last_scrape_error ?? null;

      setScrapingById((prev) => ({ ...prev, [competitorId]: true }));

      const poll = async () => {
        if (Date.now() - startedAt > SCRAPE_POLL_TIMEOUT_MS) {
          stopScrapePolling(competitorId);
          showError('Scrape is taking longer than expected. Refresh to check status.');
          return;
        }

        try {
          const status = await competitorsAPI.getScrapingStatus(competitorId);
          updateCompetitorScrapeFields(competitorId, status);

          const scrapedChanged =
            Boolean(status.last_scraped_at) &&
            status.last_scraped_at !== baselineScrapedAt;
          const errorChanged =
            (status.last_scrape_error || null) !== (baselineError || null);

          if (scrapedChanged || errorChanged) {
            stopScrapePolling(competitorId);
            if (status.last_scrape_error) {
              showError(
                `Scrape finished with errors: ${String(status.last_scrape_error).slice(0, 200)}`
              );
            } else if (scrapedChanged) {
              showSuccess('Competitor scrape completed.');
            }
          }
        } catch (error) {
          stopScrapePolling(competitorId);
          showError(
            error.response?.data?.detail ||
              error.message ||
              'Failed to check scrape status'
          );
        }
      };

      poll();
      scrapePollTimers.current[competitorId] = setInterval(
        poll,
        SCRAPE_POLL_INTERVAL_MS
      );
    },
    [showError, showSuccess, stopScrapePolling, updateCompetitorScrapeFields]
  );

  const handleScrapeNow = useCallback(
    async (competitor) => {
      const competitorId = competitor.competitor_id;
      if (scrapingById[competitorId]) return;

      try {
        const out = await competitorsAPI.triggerScrape(competitorId);
        if (out.task_id) {
          showSuccess(
            `Scrape queued (task ${String(out.task_id).slice(0, 8)}…). Updates run in the background.`
          );
        } else {
          showSuccess('Scrape requested.');
        }
        startScrapePolling(competitorId, {
          last_scraped_at: competitor.last_scraped_at,
          last_scrape_error: competitor.last_scrape_error,
        });
      } catch (error) {
        showError(
          error.response?.data?.detail ||
            error.message ||
            'Could not queue competitor scrape'
        );
      }
    },
    [scrapingById, showError, showSuccess, startScrapePolling]
  );

  // Load data on component mount
  useEffect(() => {
    console.log('🔄 Loading competitors and clients...');
    loadCompetitors();
  }, []);

  // Reload competitors when filters change
  useEffect(() => {
    console.log('🔄 Filters changed, reloading competitors...', filters);
    loadCompetitors();
  }, [filters.client_id, filters.industry, filters.threat_level, filters.competitor_type, filters.search]);

  // Memoize clients to prevent unnecessary re-renders
  const memoizedClients = useMemo(() => clients, [clients]);

  const handleAutonomousDiscovery = async () => {
    const clientId =
      filters.client_id === null ||
      filters.client_id === undefined ||
      filters.client_id === ''
        ? null
        : Number(filters.client_id);
    if (!clientId || Number.isNaN(clientId)) {
      showError('Please select a client from the dropdown first');
      return;
    }

    setDiscovering(true);
    try {
      const response = await competitorsAPI.discoverForClient(clientId);
      const found = Number(response?.competitors_found ?? 0);
      showSuccess(
        response?.message ||
          `Discovered ${found} competitor${found === 1 ? '' : 's'} for this client.`
      );
      await loadCompetitors({ background: true });
    } catch (error) {
      console.error('❌ Error discovering competitors:', error);
      showError(
        formatApiError(error, 'Failed to discover competitors. Please try again.')
      );
    } finally {
      setDiscovering(false);
    }
  };

  const loadCompetitors = async (options) => {
    const background = Boolean(options && options.background === true);
    try {
      if (!background) setLoading(true);
      console.log(
        '🚀 LOADING COMPETITORS - NEW VERSION:',
        new Date().toISOString()
      );
      // Ensure client_id is a number in filters, and remove null/empty values
      const filtersToSend = {};
      // client_id can be a number or a string that needs parsing
      if (filters.client_id !== null && filters.client_id !== undefined && filters.client_id !== '') {
        const clientId = typeof filters.client_id === 'number' 
          ? filters.client_id 
          : parseInt(filters.client_id, 10);
        if (!isNaN(clientId)) {
          filtersToSend.client_id = clientId;
        }
      }
      if (filters.industry) {
        filtersToSend.industry = filters.industry;
      }
      if (filters.threat_level) {
        filtersToSend.threat_level = filters.threat_level;
      }
      if (filters.competitor_type) {
        filtersToSend.competitor_type = filters.competitor_type;
      }
      if (filters.search) {
        filtersToSend.search = filters.search;
      }
      
      console.log('Loading competitors with filters:', filtersToSend);
      const data = await competitorsAPI.getCompetitors(filtersToSend);
      const list = Array.isArray(data) ? data : [];
      console.log('✅ Competitors loaded successfully:', list.length, 'items');
      setCompetitors(list);
      if (!Array.isArray(data) && data != null) {
        showError(data?.detail || 'Competitors response was invalid');
      }
    } catch (error) {
      console.error('❌ Error loading competitors:', error);
      console.error('❌ Error details:', error.response?.data);
      setCompetitors([]);
      showError(
        error.response?.data?.detail ||
          error.message ||
          'Failed to load competitors'
      );
    } finally {
      if (!background) setLoading(false);
      console.log('🏁 Loading finished');
    }
  };

  const handleCreateCompetitor = useCallback(async () => {
    try {
      // Validate required fields
      if (!formData.name || formData.name.trim() === '') {
        showError('Competitor name is required');
        return;
      }
      if (!formData.industry || formData.industry.trim() === '') {
        showError('Industry is required');
        return;
      }
      
      // Transform data to match backend schema
      const dataToSend = {
        name: formData.name.trim(),
        industry: formData.industry.trim(),
        // Convert empty strings to null for optional fields
        company_name: formData.company_name?.trim() || null,
        website: formData.website?.trim() || null,
        facebook_url: formData.facebook_url?.trim() || null,
        instagram_url: formData.instagram_url?.trim() || null,
        linkedin_url: formData.linkedin_url?.trim() || null,
        youtube_url: formData.youtube_url?.trim() || null,
        twitter_url: formData.twitter_url?.trim() || null,
        tiktok_url: formData.tiktok_url?.trim() || null,
        target_audience: formData.target_audience?.trim() || null,
        company_size: formData.company_size?.trim() || null,
        location: formData.location?.trim() || null,
        notes: formData.notes?.trim() || null,
        competitor_type: formData.competitor_type || 'direct',
        threat_level: formData.threat_level || 'medium',
        // Convert numbers - ensure they're numbers, not strings
        market_share: formData.market_share ? parseFloat(formData.market_share) : 0,
        budget_estimate: formData.budget_estimate ? parseFloat(formData.budget_estimate) : null,
        content_quality_score: formData.content_quality_score ? parseFloat(formData.content_quality_score) : 0,
        brand_sentiment: formData.brand_sentiment ? parseFloat(formData.brand_sentiment) : 0,
        // Ensure client_id is a number or null
        client_id: formData.client_id ? parseInt(formData.client_id, 10) : null,
      };
      
      console.log('Sending competitor data:', dataToSend);
      
      await competitorsAPI.createCompetitor(dataToSend);
      showSuccess('Competitor created successfully');
      setOpenDialog(false);
      
      // Ensure the filter is set to the client_id used to create the competitor
      // This ensures the new competitor will be visible after reload
      if (dataToSend.client_id) {
        setFilters((prev) => ({
          ...prev,
          client_id: dataToSend.client_id,
        }));
        // The useEffect will automatically reload when filters change
      } else {
        // If no client_id, just reload with current filters
        await loadCompetitors();
      }
    } catch (error) {
      console.error('Error creating competitor:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create competitor';
      showError(errorMessage);
    }
  }, [formData, showSuccess, showError, loadCompetitors]);

  const handleUpdateCompetitor = useCallback(async () => {
    if (!selectedCompetitor) return;
    try {
      // Transform data similar to create
      const dataToSend = {
        name: formData.name?.trim() || undefined,
        industry: formData.industry?.trim() || undefined,
        company_name: formData.company_name?.trim() || null,
        website: formData.website?.trim() || null,
        facebook_url: formData.facebook_url?.trim() || null,
        instagram_url: formData.instagram_url?.trim() || null,
        linkedin_url: formData.linkedin_url?.trim() || null,
        youtube_url: formData.youtube_url?.trim() || null,
        twitter_url: formData.twitter_url?.trim() || null,
        tiktok_url: formData.tiktok_url?.trim() || null,
        target_audience: formData.target_audience?.trim() || null,
        company_size: formData.company_size?.trim() || null,
        location: formData.location?.trim() || null,
        notes: formData.notes?.trim() || null,
        competitor_type: formData.competitor_type || undefined,
        threat_level: formData.threat_level || undefined,
        market_share: formData.market_share !== undefined ? parseFloat(formData.market_share) : undefined,
        budget_estimate: formData.budget_estimate !== undefined ? (formData.budget_estimate ? parseFloat(formData.budget_estimate) : null) : undefined,
        content_quality_score: formData.content_quality_score !== undefined ? parseFloat(formData.content_quality_score) : undefined,
        brand_sentiment: formData.brand_sentiment !== undefined ? parseFloat(formData.brand_sentiment) : undefined,
      };
      
      // Remove undefined values
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === undefined) {
          delete dataToSend[key];
        }
      });
      
      await competitorsAPI.updateCompetitor(
        selectedCompetitor.competitor_id,
        dataToSend
      );
      showSuccess('Competitor updated successfully');
      setOpenDialog(false);
      loadCompetitors();
    } catch (error) {
      console.error('Error updating competitor:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update competitor';
      showError(errorMessage);
    }
  }, [selectedCompetitor, formData, showSuccess, showError, loadCompetitors]);

  const handleDeleteCompetitor = (competitorId) => {
    setCompetitorToDelete(competitorId);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!competitorToDelete) return;
    
    // Find the competitor to delete for optimistic update
    const competitorToRemove = competitors.find(
      (c) => c.competitor_id === competitorToDelete
    );
    
    // Optimistically remove from UI immediately for smooth UX
    setCompetitors((prev) =>
      prev.filter((c) => c.competitor_id !== competitorToDelete)
    );
    
    // Close modal immediately
    setDeleteModalOpen(false);
    const deletedId = competitorToDelete;
    setCompetitorToDelete(null);
    
    // Make API call in background
    try {
      console.log('🗑️ Attempting to delete competitor:', deletedId);
      await competitorsAPI.deleteCompetitor(deletedId);
      console.log('✅ Delete result: success');
      // Success - item already removed from UI, just show success message
      showSuccess('Competitor deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting competitor:', error);
      console.error('❌ Error details:', error.response?.data);
      
      // Restore the competitor if deletion failed
      if (competitorToRemove) {
        setCompetitors((prev) => {
          // Find the position to insert back (maintain order)
          const index = prev.findIndex(
            (c) => c.competitor_id > competitorToRemove.competitor_id
          );
          if (index === -1) {
            // Add to end
            return [...prev, competitorToRemove];
          }
          // Insert at original position
          return [
            ...prev.slice(0, index),
            competitorToRemove,
            ...prev.slice(index),
          ];
        });
      }
      
      showError(
        `Failed to delete competitor: ${
          error.response?.data?.detail || error.message
        }`
      );
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setCompetitorToDelete(null);
  };

  const handleOpenDialog = (mode, competitor = null) => {
    setDialogMode(mode);
    setSelectedCompetitor(competitor);

    if (mode === 'create') {
      // Pre-fill client_id from filters if one is selected
      const prefillClientId = filters.client_id || '';
      
      setFormData({
        name: '',
        company_name: '',
        website: '',
        facebook_url: '',
        instagram_url: '',
        linkedin_url: '',
        youtube_url: '',
        twitter_url: '',
        tiktok_url: '',
        industry: '',
        target_audience: '',
        company_size: '',
        location: '',
        notes: '',
        competitor_type: 'direct',
        threat_level: 'medium',
        market_share: 0,
        budget_estimate: 0,
        content_quality_score: 0,
        brand_sentiment: 0,
        client_id: prefillClientId,
      });
    } else if (mode === 'edit' && competitor) {
      setFormData({
        name: competitor.name,
        company_name: competitor.company_name,
        website: competitor.website,
        facebook_url: competitor.facebook_url,
        instagram_url: competitor.instagram_url,
        linkedin_url: competitor.linkedin_url,
        youtube_url: competitor.youtube_url,
        twitter_url: competitor.twitter_url,
        tiktok_url: competitor.tiktok_url,
        industry: competitor.industry,
        target_audience: competitor.target_audience,
        company_size: competitor.company_size,
        location: competitor.location,
        notes: competitor.notes,
        competitor_type: competitor.competitor_type,
        threat_level: competitor.threat_level,
        market_share: competitor.market_share,
        budget_estimate: competitor.budget_estimate,
        content_quality_score: competitor.content_quality_score,
        brand_sentiment: competitor.brand_sentiment,
        client_id: competitor.client_id,
      });
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setSelectedCompetitor(null);
  }, []);

  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Special handler for number fields to prevent leading zeros
  const handleNumberChange = useCallback((field, value) => {
    // If the value is empty or just whitespace, set to 0
    if (value === '' || value === null || value === undefined) {
      setFormData((prev) => ({
        ...prev,
        [field]: 0,
      }));
      return;
    }
    
    // Convert to number, handling string inputs
    const numValue = typeof value === 'string' 
      ? parseFloat(value) || 0 
      : value;
    
    setFormData((prev) => ({
      ...prev,
      [field]: numValue,
    }));
  }, []);

  const getThreatLevelColor = (level) => {
    switch (level) {
      case 'low':
        return 'success';
      case 'medium':
        return 'warning';
      case 'high':
        return 'error';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  const getCompetitorTypeColor = (type) => {
    switch (type) {
      case 'direct':
        return 'error';
      case 'indirect':
        return 'warning';
      case 'aspirational':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUpIcon color="success" />;
      case 'decreasing':
        return <TrendingDownIcon color="error" />;
      case 'stable':
        return <TrendingFlatIcon color="info" />;
      default:
        return <TrendingFlatIcon />;
    }
  };

  const CompetitorCardWithML = ({ competitor }) => {
    // Use values directly from competitor object instead of fetching from ML endpoints

    return (
      <Card sx={{ mb: 2, '&:hover': { boxShadow: 3 } }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {competitor.name}
              </Typography>
              {competitor.company_name && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {competitor.company_name}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  label={competitor.industry}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={competitor.competitor_type}
                  size="small"
                  color={getCompetitorTypeColor(competitor.competitor_type)}
                />
                <Chip
                  label={competitor.threat_level}
                  size="small"
                  color={getThreatLevelColor(competitor.threat_level)}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="View Details">
                <IconButton
                  size="small"
                  onClick={() => handleOpenDialog('view', competitor)}
                >
                  <ViewIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => handleOpenDialog('edit', competitor)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() =>
                    handleDeleteCompetitor(competitor.competitor_id)
                  }
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Social Media Links */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Social Media Links:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {competitor.facebook_url && (
                <Chip
                  label="Facebook"
                  size="small"
                  clickable
                  onClick={() => window.open(competitor.facebook_url, '_blank')}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#1877f2', color: 'white' },
                  }}
                />
              )}
              {SHOW_INSTAGRAM_UI && competitor.instagram_url && (
                <Chip
                  label="Instagram"
                  size="small"
                  clickable
                  onClick={() => window.open(competitor.instagram_url, '_blank')}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#e4405f', color: 'white' },
                  }}
                />
              )}
              {competitor.linkedin_url && (
                <Chip
                  label="LinkedIn"
                  size="small"
                  clickable
                  onClick={() => window.open(competitor.linkedin_url, '_blank')}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#0077b5', color: 'white' },
                  }}
                />
              )}
              {competitor.youtube_url && (
                <Chip
                  label="YouTube"
                  size="small"
                  clickable
                  onClick={() => window.open(competitor.youtube_url, '_blank')}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#ff0000', color: 'white' },
                  }}
                />
              )}
              {competitor.twitter_url && (
                <Chip
                  label="X/Twitter"
                  size="small"
                  clickable
                  onClick={() => window.open(competitor.twitter_url, '_blank')}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#000000', color: 'white' },
                  }}
                />
              )}
              {competitor.tiktok_url && (
                <Chip
                  label="TikTok"
                  size="small"
                  clickable
                  onClick={() => window.open(competitor.tiktok_url, '_blank')}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#000000', color: 'white' },
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Scrape status */}
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Last scraped
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body1">
                    {formatScrapeTimestamp(competitor.last_scraped_at)}
                  </Typography>
                  {isScrapeStale(competitor.last_scraped_at) ? (
                    <Chip
                      label="Stale"
                      size="small"
                      color="warning"
                      icon={<WarningIcon />}
                    />
                  ) : (
                    <Chip
                      label="Fresh"
                      size="small"
                      color="success"
                      icon={<CheckCircleIcon />}
                    />
                  )}
                  <Chip
                    label="Public metrics only"
                    size="small"
                    variant="outlined"
                    sx={{ borderColor: '#30363d', color: '#8b949e' }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Chargeable scrapes: Facebook, Instagram, YouTube. LinkedIn / X are experimental;
                  TikTok is deferred. Fresh means usable posts were persisted — not HTTP 200 alone.
                </Typography>
                {competitor.last_scrape_error && (
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ mt: 1, maxWidth: 560 }}
                  >
                    Last error: {String(competitor.last_scrape_error).slice(0, 500)}
                  </Typography>
                )}
              </Box>
              <Button
                variant="outlined"
                size="small"
                sx={{ textTransform: 'none', minWidth: 110 }}
                disabled={!competitor.active || Boolean(scrapingById[competitor.competitor_id])}
                onClick={() => handleScrapeNow(competitor)}
                startIcon={
                  scrapingById[competitor.competitor_id] ? (
                    <CircularProgress size={16} />
                  ) : (
                    <RefreshIcon />
                  )
                }
              >
                {scrapingById[competitor.competitor_id] ? 'Scraping…' : 'Scrape now'}
              </Button>
            </Box>
          </Box>

          {/* Metrics */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Market Share
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {competitor.market_share 
                    ? `${parseFloat(competitor.market_share).toFixed(1)}%`
                    : '0%'}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Quality Score
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {competitor.content_quality_score 
                    ? `${(parseFloat(competitor.content_quality_score) * 100).toFixed(1)}%`
                    : '0%'}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Sentiment
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {competitor.brand_sentiment !== null && competitor.brand_sentiment !== undefined
                    ? `${parseFloat(competitor.brand_sentiment) > 0 ? '+' : ''}${parseFloat(competitor.brand_sentiment).toFixed(2)}`
                    : '0.00'}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={competitor.active ? 'Active' : 'Inactive'}
              color={competitor.active ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  const CompetitorCard = ({ competitor }) => (
    <CompetitorCardWithML competitor={competitor} />
  );


  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Competitor Analysis
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('create')}
          sx={{ mr: 2 }}
        >
          Add Competitor
        </Button>
        <Button
          variant="contained"
          startIcon={
            discovering ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SearchIcon />
            )
          }
          onClick={handleAutonomousDiscovery}
          disabled={!filters.client_id || discovering}
          sx={{
            backgroundColor: filters.client_id ? '#4caf50' : '#9e9e9e',
            borderColor: filters.client_id ? '#4caf50' : '#9e9e9e',
            color: 'white',
            fontWeight: 600,
            px: 3,
            py: 1.5,
            '&:hover': {
              backgroundColor: filters.client_id ? '#45a049' : '#9e9e9e',
            },
            '&:disabled': {
              backgroundColor: '#9e9e9e',
              color: 'white',
            },
          }}
        >
          {discovering
            ? 'Discovering…'
            : filters.client_id
              ? `🤖 Auto-Discover for Client ${filters.client_id}`
              : '🤖 Select Client First'}
        </Button>
      </Box>

      {discovering && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Searching for competitors. This usually takes a few seconds.
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Filters
          </Typography>

          {/* Filter Controls */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              mb: 3,
            }}
          >
            {/* First Row - Filter Dropdowns */}
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexWrap: 'wrap',
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ minWidth: 200, flex: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Client</InputLabel>
                  <Select
                    value={filters.client_id || ''}
                    onChange={(e) => {
                      const clientId = e.target.value ? parseInt(e.target.value, 10) : null;
                      console.log('Client selected:', clientId);
                      setFilters((prev) => ({
                        ...prev,
                        client_id: clientId,
                      }));
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">All Clients</MenuItem>
                    {clients && clients.length > 0 ? (
                      clients.map((client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>Loading clients...</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ minWidth: 200, flex: 1 }}>
                <TextField
                  fullWidth
                  label="Industry"
                  value={filters.industry}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      industry: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ minWidth: 200, flex: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Threat Level</InputLabel>
                  <Select
                    value={filters.threat_level}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        threat_level: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">All Levels</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ minWidth: 200, flex: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Competitor Type</InputLabel>
                  <Select
                    value={filters.competitor_type}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        competitor_type: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="direct">Direct</MenuItem>
                    <MenuItem value="indirect">Indirect</MenuItem>
                    <MenuItem value="aspirational">Aspirational</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Second Row - Search and Actions */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 300 }}>
                <TextField
                  fullWidth
                  label="Search"
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                  }
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                />
              </Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadCompetitors}
                sx={{ minWidth: 120 }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Competitors List */}
      {competitors.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No Competitors Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Add your first competitor to start analyzing the competition
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {competitors.map((competitor) => (
            <Box key={competitor.competitor_id} sx={{ width: '100%' }}>
              <CompetitorCard competitor={competitor} />
            </Box>
          ))}
        </Box>
      )}

      {/* Dialog - Inline to prevent re-renders */}
      {openDialog && (
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          disableEnforceFocus
          disableAutoFocus
          keepMounted={false}
        >
          <DialogTitle>
            {dialogMode === 'create' && 'Add New Competitor'}
            {dialogMode === 'edit' && 'Edit Competitor'}
            {dialogMode === 'view' && 'Competitor Details'}
          </DialogTitle>
          <DialogContent sx={{ p: 0 }} dividers={false}>
            <Box sx={{ p: 3 }}>
              {/* Basic Information Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 4, fontWeight: 600, color: 'primary.main' }}
                >
                  Basic Information
                </Typography>

                {/* Company Details */}
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 3, fontWeight: 500, color: 'text.secondary' }}
                  >
                    Company Details
                  </Typography>

                  {/* First Row - Client Dropdown (Full Width) */}
                  <Box sx={{ mb: 4 }}>
                    <FormControl fullWidth variant="outlined" size="large">
                      <InputLabel>Client</InputLabel>
                      <Select
                        value={formData.client_id}
                        onChange={(e) =>
                          handleFormChange('client_id', e.target.value)
                        }
                        disabled={dialogMode === 'view'}
                      >
                        {(memoizedClients || []).map((client) => (
                          <MenuItem key={client.id} value={client.id}>
                            {client.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Second Row - Three Text Fields */}
                  <Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Competitor Name"
                        value={formData.name}
                        onChange={(e) => handleFormChange('name', e.target.value)}
                        disabled={dialogMode === 'view'}
                        required
                        variant="outlined"
                        size="large"
                        helperText="Enter the competitor's brand name"
                        autoComplete="off"
                      />
                      <TextField
                        fullWidth
                        label="Company Name"
                        value={formData.company_name}
                        onChange={(e) =>
                          handleFormChange('company_name', e.target.value)
                        }
                        disabled={dialogMode === 'view'}
                        variant="outlined"
                        size="large"
                        helperText="Official company name"
                        autoComplete="off"
                      />
                      <TextField
                        fullWidth
                        label="Website"
                        value={formData.website}
                        onChange={(e) =>
                          handleFormChange('website', e.target.value)
                        }
                        disabled={dialogMode === 'view'}
                        variant="outlined"
                        size="large"
                        helperText="Company website URL"
                        autoComplete="off"
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Business Context */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 3, fontWeight: 500, color: 'text.secondary' }}
                  >
                    Business Context
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Industry"
                      value={formData.industry}
                      onChange={(e) => handleFormChange('industry', e.target.value)}
                      disabled={dialogMode === 'view'}
                      required
                      variant="outlined"
                      size="large"
                      helperText="Primary industry or sector"
                      autoComplete="off"
                    />
                    <TextField
                      fullWidth
                      label="Target Audience"
                      value={formData.target_audience}
                      onChange={(e) =>
                        handleFormChange('target_audience', e.target.value)
                      }
                      disabled={dialogMode === 'view'}
                      variant="outlined"
                      size="large"
                      helperText="Primary target demographic"
                      autoComplete="off"
                    />
                    <TextField
                      fullWidth
                      label="Company Size"
                      value={formData.company_size}
                      onChange={(e) =>
                        handleFormChange('company_size', e.target.value)
                      }
                      disabled={dialogMode === 'view'}
                      variant="outlined"
                      size="large"
                      helperText="e.g., Startup, SME, Enterprise"
                      autoComplete="off"
                    />
                    <TextField
                      fullWidth
                      label="Location"
                      value={formData.location}
                      onChange={(e) => handleFormChange('location', e.target.value)}
                      disabled={dialogMode === 'view'}
                      variant="outlined"
                      size="large"
                      helperText="Headquarters or primary location"
                      autoComplete="off"
                    />
                  </Box>
                </Box>
              </Box>

              {/* Social Media Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 4, fontWeight: 600, color: 'primary.main' }}
                >
                  Social Media Links
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Facebook URL"
                      value={formData.facebook_url}
                      onChange={(e) =>
                        handleFormChange('facebook_url', e.target.value)
                      }
                      disabled={dialogMode === 'view'}
                      variant="outlined"
                      size="large"
                      helperText="Full URL to Facebook page/profile (e.g., https://facebook.com/companyname)"
                      placeholder="https://www.facebook.com/companyname"
                      autoComplete="off"
                    />
                  </Grid>
                  {SHOW_INSTAGRAM_UI && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Instagram URL"
                        value={formData.instagram_url}
                        onChange={(e) =>
                          handleFormChange('instagram_url', e.target.value)
                        }
                        disabled={dialogMode === 'view'}
                        variant="outlined"
                        size="large"
                        helperText="Full URL to Instagram profile (e.g., https://instagram.com/companyname)"
                        placeholder="https://www.instagram.com/companyname"
                        autoComplete="off"
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="LinkedIn URL"
                      value={formData.linkedin_url}
                      onChange={(e) =>
                        handleFormChange('linkedin_url', e.target.value)
                      }
                      disabled={dialogMode === 'view'}
                      variant="outlined"
                      size="large"
                      helperText="Experimental — not in default Beat scrape until MVP contract is met"
                      placeholder="https://www.linkedin.com/company/companyname"
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="YouTube URL"
                      value={formData.youtube_url}
                      onChange={(e) =>
                        handleFormChange('youtube_url', e.target.value)
                      }
                      disabled={dialogMode === 'view'}
                      variant="outlined"
                      size="large"
                      helperText="Full URL to YouTube channel"
                      placeholder="https://www.youtube.com/@channelname"
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="X/Twitter URL"
                      value={formData.twitter_url}
                      onChange={(e) =>
                        handleFormChange('twitter_url', e.target.value)
                      }
                      disabled={dialogMode === 'view'}
                      variant="outlined"
                      size="large"
                      helperText="Experimental — not in default Beat scrape until MVP contract is met"
                      placeholder="https://x.com/username"
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="TikTok URL"
                      value={formData.tiktok_url}
                      onChange={(e) =>
                        handleFormChange('tiktok_url', e.target.value)
                      }
                      disabled={dialogMode === 'view'}
                      variant="outlined"
                      size="large"
                      helperText="Deferred — no scrape work yet (URL stored for later)"
                      placeholder="https://www.tiktok.com/@username"
                      placeholder="https://www.tiktok.com/@username"
                      autoComplete="off"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Analysis Settings Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}
                >
                  Analysis Settings
                </Typography>

                {/* Classification Row */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}
                  >
                    Classification
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl fullWidth variant="outlined" size="large">
                      <InputLabel>Competitor Type</InputLabel>
                      <Select
                        value={formData.competitor_type}
                        onChange={(e) =>
                          handleFormChange('competitor_type', e.target.value)
                        }
                        disabled={dialogMode === 'view'}
                      >
                        <MenuItem value="direct">Direct Competitor</MenuItem>
                        <MenuItem value="indirect">Indirect Competitor</MenuItem>
                        <MenuItem value="aspirational">Aspirational Brand</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth variant="outlined" size="large">
                      <InputLabel>Threat Level</InputLabel>
                      <Select
                        value={formData.threat_level}
                        onChange={(e) =>
                          handleFormChange('threat_level', e.target.value)
                        }
                        disabled={dialogMode === 'view'}
                      >
                        <MenuItem value="low">Low Threat</MenuItem>
                        <MenuItem value="medium">Medium Threat</MenuItem>
                        <MenuItem value="high">High Threat</MenuItem>
                        <MenuItem value="critical">Critical Threat</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* Market Metrics Row */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}
                  >
                    Market Metrics
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Market Share (%)"
                      type="number"
                      value={formData.market_share === 0 ? '' : formData.market_share}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === null) {
                          handleNumberChange('market_share', 0);
                        } else {
                          const numVal = parseFloat(val);
                          if (!isNaN(numVal)) {
                            handleNumberChange('market_share', numVal);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '' || e.target.value === null) {
                          handleNumberChange('market_share', 0);
                        }
                      }}
                      disabled={dialogMode === 'view'}
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      variant="outlined"
                      size="large"
                      helperText="Estimated market share percentage"
                      autoComplete="off"
                    />
                    <TextField
                      fullWidth
                      label="Budget Estimate ($)"
                      type="number"
                      value={formData.budget_estimate === 0 ? '' : formData.budget_estimate}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === null) {
                          handleNumberChange('budget_estimate', 0);
                        } else {
                          const numVal = parseFloat(val);
                          if (!isNaN(numVal)) {
                            handleNumberChange('budget_estimate', numVal);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '' || e.target.value === null) {
                          handleNumberChange('budget_estimate', 0);
                        }
                      }}
                      disabled={dialogMode === 'view'}
                      inputProps={{ min: 0, step: 1000 }}
                      variant="outlined"
                      size="large"
                      helperText="Estimated annual marketing budget"
                      autoComplete="off"
                    />
                  </Box>
                </Box>

                {/* Performance Metrics Row */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}
                  >
                    Performance Metrics
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Content Quality Score"
                      type="number"
                      value={formData.content_quality_score === 0 ? '' : formData.content_quality_score}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === null) {
                          handleNumberChange('content_quality_score', 0);
                        } else {
                          const numVal = parseFloat(val);
                          if (!isNaN(numVal)) {
                            handleNumberChange('content_quality_score', numVal);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '' || e.target.value === null) {
                          handleNumberChange('content_quality_score', 0);
                        }
                      }}
                      disabled={dialogMode === 'view'}
                      inputProps={{ min: 0, max: 1, step: 0.01 }}
                      variant="outlined"
                      size="large"
                      helperText="Score from 0.0 to 1.0"
                      autoComplete="off"
                    />
                    <TextField
                      fullWidth
                      label="Brand Sentiment"
                      type="number"
                      value={formData.brand_sentiment === 0 ? '' : formData.brand_sentiment}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === null) {
                          handleNumberChange('brand_sentiment', 0);
                        } else {
                          const numVal = parseFloat(val);
                          if (!isNaN(numVal)) {
                            handleNumberChange('brand_sentiment', numVal);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '' || e.target.value === null) {
                          handleNumberChange('brand_sentiment', 0);
                        }
                      }}
                      disabled={dialogMode === 'view'}
                      inputProps={{ min: -1, max: 1, step: 0.01 }}
                      variant="outlined"
                      size="large"
                      helperText="Range from -1.0 (negative) to 1.0 (positive)"
                      autoComplete="off"
                    />
                  </Box>
                </Box>
              </Box>

              {/* Notes Section */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 4, fontWeight: 600, color: 'primary.main' }}
                >
                  Additional Notes
                </Typography>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={5}
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  disabled={dialogMode === 'view'}
                  variant="outlined"
                  size="large"
                  placeholder="Add any additional notes about this competitor, their strategy, strengths, weaknesses, or any other relevant information..."
                  helperText="Use this space to add detailed notes about the competitor's strategy, market position, or any other relevant insights"
                  autoComplete="off"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{ minWidth: 100 }}
            >
              {dialogMode === 'view' ? 'Close' : 'Cancel'}
            </Button>
            {dialogMode !== 'view' && (
              <Button
                onClick={
                  dialogMode === 'create'
                    ? handleCreateCompetitor
                    : handleUpdateCompetitor
                }
                variant="contained"
                sx={{ minWidth: 100 }}
              >
                {dialogMode === 'create'
                  ? 'Create Competitor'
                  : 'Update Competitor'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Competitor</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this competitor? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Competitors;
